import * as React from "react"
import type { EntityTransaction, Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { vTableCellRender } from "@digits-shared/components/UI/VirtualizedTable/VTableCell"
import { findColumnIndex } from "@digits-shared/components/UI/VirtualizedTable/vTableHelpers"
import { createColumnHelper } from "@tanstack/react-table"
import { CategoryTableHeader } from "src/frontend/components/Shared/Transactions/Categories/CategoryTableHeader"
import { type CategoryTableMetadata } from "src/frontend/components/Shared/Transactions/Categories/categoryTableTypes"
import { LoadingAmountCell } from "src/frontend/components/Shared/Transactions/TableCells/AmountCell"
import { BalanceCell } from "src/frontend/components/Shared/Transactions/TableCells/BalanceCell"
import {
  type ColumnOptions,
  isTransaction,
  type TableVariant,
} from "src/frontend/components/Shared/Transactions/types"
import { useTableColumns } from "src/frontend/components/Shared/Transactions/useTableColumns"

type CategoryColumns = ColumnOptions & {
  variant: TableVariant | undefined
  hasFooter: boolean
}

export function useCategoryTransactionsTableColumns({
  variant,
  hasFooter,
  ...options
}: CategoryColumns) {
  const columns = useTableColumns(options)

  return React.useMemo(() => {
    const columnHelper = createColumnHelper<Transaction | EntityTransaction>()

    const categoryColumns = columns.slice()
    categoryColumns.push(
      columnHelper.accessor("runningTotal", {
        header: "Balance",
        size: 120,
        cell: vTableCellRender({
          LoaderCell: LoadingAmountCell,
          Cell: ({ table, data }) => {
            const { categorySummary } = table.options.meta as CategoryTableMetadata
            if (!isTransaction(data) || !categorySummary?.summary) return null
            return (
              <BalanceCell
                transaction={data}
                summary={categorySummary.summary}
                categoryType={categorySummary.dimension.type}
              />
            )
          },
        }),
      })
    )

    if (variant === "banksAndCards") {
      const displayCategoryIndex = findColumnIndex("displayCategory", categoryColumns)
      if (displayCategoryIndex !== -1) {
        categoryColumns.splice(displayCategoryIndex, 1)
      }
    }

    return [
      columnHelper.group({
        id: "categoryHeader",
        header: ({ table }) => {
          const { collapsing, exportOptions, categorySummary, loading } = table.options
            .meta as CategoryTableMetadata
          return (
            <CategoryTableHeader
              categorySummary={categorySummary}
              loading={loading}
              collapsing={collapsing}
              exportOptions={exportOptions}
            />
          )
        },
        footer: hasFooter
          ? ({ table }) => {
              const { categorySummary, loading, Footer } = table.options
                .meta as CategoryTableMetadata
              if (!Footer) return null
              return <Footer categorySummary={categorySummary} loading={loading} />
            }
          : undefined,
        columns: categoryColumns,
      }),
    ]
  }, [columns, hasFooter, variant])
}
